import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { IMAGES, WHATSAPP_URL, scrollToSection } from "./constants";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden bg-ebony"
    >
      <motion.div className="absolute inset-0" style={{ y: imgY }}>
        <motion.img
          src={IMAGES.hero}
          alt="Dressage horse in collected frame, fine-art black and white"
          data-testid="hero-image"
          className="h-full w-full scale-105 object-cover object-[70%_center] opacity-90 lg:object-right"
          initial={{ opacity: 0, scale: 1.14 }}
          animate={{ opacity: 0.9, scale: 1.05 }}
          transition={{ duration: 2, ease: EASE }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ebony via-ebony/70 to-ebony/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-ebony via-transparent to-ebony/60" />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-32 pb-24 lg:px-10"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="mb-10 flex items-center gap-4"
        >
          <span className="h-px w-16 bg-gold/60" />
          <span className="text-xs uppercase tracking-[0.35em] text-champagne">
            On-Site Training · Dubai
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="max-w-4xl font-serif text-5xl font-light leading-[0.98] tracking-tight text-offwhite sm:text-6xl lg:text-8xl"
        >
          <MaskedLine delay={0.35}>Elite training for</MaskedLine>
          <MaskedLine delay={0.5}>horse &amp; female rider —</MaskedLine>
          <MaskedLine delay={0.65}>
            in the <em className="italic text-gold">German tradition.</em>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subheadline"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.05, ease: EASE }}
          className="mt-10 max-w-xl text-base font-light leading-relaxed text-cream/70 md:text-lg"
        >
          Kiara Samson — twenty years in the saddle, partially trained under
          a German Grand Prix dressage rider — now training horses and their
          female riders on-site at private stables across Dubai.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.25, ease: EASE }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <button
            data-testid="hero-cta-button"
            onClick={() => scrollToSection("#consultation")}
            className="group relative overflow-hidden border border-gold bg-gold px-9 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-ebony transition-colors duration-500 hover:bg-gold/90"
          >
            Reserve Winter Training
          </button>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-link"
            className="flex items-center gap-3 border border-champagne/30 px-9 py-4 text-xs uppercase tracking-[0.25em] text-cream/80 transition-colors duration-500 hover:border-gold hover:text-gold"
          >
            <MessageCircle size={15} />
            WhatsApp Kiara
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-10 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3"
      >
        <span className="text-[10px] uppercase tracking-[0.35em] text-cream/40">
          Scroll
        </span>
        <div className="h-14 w-px overflow-hidden bg-cream/10">
          <div className="h-full w-full animate-scroll-cue bg-gold/70" />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
